// ============================================================
// SpawnedEntity — dynamically spawned word objects
// Each entity reads its `special` tags and runs real behaviors
// ============================================================
import * as PIXI from 'pixi.js';
import { PhysicsBody } from '../physics/PhysicsBody.js';
import { Z } from '../constants.js';
import { sound } from '../systems/SoundEngine.js';

let _eid = 0;

export class SpawnedEntity {
  constructor(config, x, y, container) {
    this.id        = ++_eid;
    this.config    = config;
    this.container = container;
    this.alive     = true;
    this._timers   = {};
    this._player   = null;    // set by WordSystem after spawn
    this._attached = false;   // balloon carrying player

    // ── Physics body ──
    // Platform-type objects are heavy so they don't shake when walked on.
    // Creatures / items are lighter so they react to being pushed.
    const massScale = ['platform','vehicle'].includes(config.type) ? 3 : 1;

    this.body = new PhysicsBody({
      x, y,
      width:  config.w,
      height: config.h,
      mass:        (config.mass ?? 5) * massScale,
      bounciness:  config.bounciness  ?? 0,
      friction:    config.friction    ?? 0.8,
      isStatic:    config.isStatic    ?? false,
      noGravity:   config.noGravity   ?? false,
      gravityScale:config.gravityScale ?? 1,
      canFloat:    config.canFloat    ?? false,
      material:    config.material    ?? 'default',
    });
    this.body.entity = this;

    // ── PIXI ──
    this.gfx = new PIXI.Container();
    this.gfx.zIndex = Z.spawned;
    container.addChild(this.gfx);
    this._draw();
    this._setupBehaviors();
  }

  // ─────────────────────────────────────────────────────────
  // Drawing
  // ─────────────────────────────────────────────────────────
  _draw() {
    const { w, h, color, shape, alpha = 1, rainbow, type, emoji } = this.config;
    const c = rainbow ? this._rainbowColor() : (color ?? 0x808080);

    const bg = new PIXI.Graphics();
    if (shape === 'circle') {
      const r = Math.min(w, h) / 2;
      bg.circle(0, 0, r).fill({ color: c, alpha });
      bg.circle(0, 0, r).stroke({ color: 0x000000, alpha: 0.25, width: 2 });
    } else {
      const hw = w / 2, hh = h / 2;
      bg.rect(-hw, -hh, w, h).fill({ color: c, alpha });
      bg.rect(-hw, -hh, w, h).stroke({ color: 0x000000, alpha: 0.2, width: 2 });
      bg.rect(-hw, -hh, w, 5).fill({ color: 0xFFFFFF, alpha: 0.2 }); // top sheen
    }
    this.gfx.addChild(bg);
    this._bg = bg;

    // Emoji icon
    const icon = new PIXI.Text({
      text: emoji ?? '?',
      style: { fontSize: Math.min(w, h) * 0.55 },
    });
    icon.anchor.set(0.5, 0.5);
    this.gfx.addChild(icon);

    // Modifier badge
    if (this.config.modLabel) {
      const badge = new PIXI.Text({
        text: this.config.modLabel,
        style: { fontSize: 11, fill: 0xFFFFFF, fontWeight: 'bold',
                 stroke: { color: 0x000000, width: 3 } },
      });
      badge.anchor.set(0.5, 1);
      badge.y = -h / 2 - 4;
      this.gfx.addChild(badge);
    }
  }

  _rainbowColor() {
    const t = Date.now() / 400;
    return ((Math.floor(Math.sin(t)   * 127 + 128) << 16) |
            (Math.floor(Math.sin(t+2) * 127 + 128) <<  8) |
             Math.floor(Math.sin(t+4) * 127 + 128));
  }

  // ─────────────────────────────────────────────────────────
  // Behavior setup — reads the `special` array from config
  // ─────────────────────────────────────────────────────────
  _setupBehaviors() {
    const s = this.config.special ?? [];

    // ── TRAMPOLINE / SPRING ──────────────────────────────
    // Make the physics body a proper bouncy surface.
    // When the player (or any body) lands on it, they get launched.
    if (s.includes('bouncy') || this.config.type === 'platform' && this.config.bounciness > 0.5) {
      // The body's bounciness is read by resolveDynamic in PhysicsEngine.
      // We also install a collision callback for extra launch force.
      this.body.onCollide = (other, normal) => {
        if (normal?.ny === 1 && other.vy < -50) {
          // other landed on us — already bounced in physics, play sound
          sound.jump();
        }
      };
    }

    // ── LADDER / ROPE ────────────────────────────────────
    // Climbable objects become a trigger zone (no blocking),
    // and the player's vertical input moves them upward.
    if (s.includes('climbable') || s.includes('swingable')) {
      this.body.isTrigger  = true;   // don't block — player walks inside
      this.body.isStatic   = true;
      this._isClimbable    = true;
      // onTrigger fires every frame while player is inside
      this.body.onTrigger  = (other) => {
        if (other.entity?._isPlayer) {
          other.entity._insideLadder = true;
          other.entity._ladderEntity = this;
        }
      };
    }

    // ── BALLOON ─────────────────────────────────────────
    // Floats upward.  If the player walks under it and jumps,
    // they attach and get carried up.  Jump again to release.
    if (s.includes('lift') || s.includes('floaty')) {
      this._isBalloon = true;
      this.body.onTrigger = (other) => {
        if (other.entity?._isPlayer && !this._attached) {
          this._attached = true;
          this._carrier  = other;
        }
      };
      // Make it a trigger so player can enter it
      this.body.isTrigger = true;
    }

    // ── NPC AI ──────────────────────────────────────────
    if (this.config.ai) {
      this._aiDir   = Math.random() < 0.5 ? 1 : -1;
      this._aiTimer = 1 + Math.random() * 2;
    }

    // ── EXPLODE timer ────────────────────────────────────
    if (s.includes('explosive') || s.includes('timer_3s')) {
      this._timers.explode = 3.0;
    }

    // ── DAMAGE on touch ──────────────────────────────────
    if (s.includes('damage') || s.includes('fire') || s.includes('lava')) {
      this.body.onTrigger = (other) => {
        if (other.entity?.hurt) other.entity.hurt();
      };
      this.body.isTrigger = true;
    }
  }

  // ─────────────────────────────────────────────────────────
  // Per-frame update
  // ─────────────────────────────────────────────────────────
  update(dt) {
    if (!this.alive) return;

    if (this.config.rainbow) this._bg.tint = this._rainbowColor();

    // ── NPC wandering ──
    if (this.config.ai === 'wander') {
      this._aiTimer -= dt;
      if (this._aiTimer <= 0 || this.body.onWall) {
        this._aiDir   = this.body.onWall ? -this._aiDir : (Math.random() < 0.5 ? 1 : -1);
        this._aiTimer = 1 + Math.random() * 2;
      }
      if (this.body.onGround) this.body.vx = this._aiDir * 70;
    }

    // ── Helper AI — move toward player ──
    if (this.config.ai === 'helper' && this._player) {
      const dx = this._player.body.x - this.body.x;
      if (this.body.onGround && Math.abs(dx) > 30) {
        this.body.vx = Math.sign(dx) * 90;
      }
    }

    // ── Flying (helicopter, dragon) ──
    if (this.config.special?.includes('flies') ||
        this.config.special?.includes('propelled')) {
      if (this.body.noGravity) {
        // noGravity entities (helicopter): gravity isn't applied at all,
        // so a huge ay rockets them up. Use a tiny push instead.
        this.body.ay -= 60;                     // ~60 px/s² upward drift
      } else {
        // Normal gravity entities (dragon): just barely beat gravity for a slow rise
        this.body.ay -= 1830;                   // net = 1830-1800 = 30 px/s² up
      }
      // Air resistance — prevents unlimited acceleration
      this.body.vy *= 0.94;
      if (this._player && this.config.ai === 'helper') {
        const dx = this._player.body.x - this.body.x;
        const dy = this._player.body.y - this.body.y - 80;
        this.body.ax += dx * 2;
        this.body.ay += Math.min(dy * 2, 500);
      }
    }

    // ── Balloon carry ──
    // carryForce=1980 tested to give ~84 px/s terminal upward (see test-flying.js)
    if (this._isBalloon && this._attached && this._carrier) {
      this._carrier.ay -= 1980;       // just enough to lift gently (net = 180 px/s²)
      this._carrier.vy *= 0.97;       // damping → terminal ≈ 100 px/s upward
      this.body.x = this._carrier.x;
      this.body.y = this._carrier.y - this.config.h - 20;
    }

    // ── Bomb countdown ──
    if (this._timers.explode !== undefined) {
      this._timers.explode -= dt;
      if (this._timers.explode < 1.2) {
        const pulse = 1 + Math.sin(this._timers.explode * 20) * 0.15;
        this.gfx.scale.set(pulse);
      }
      if (this._timers.explode <= 0) { this._explode(); return; }
    }

    // Sync graphics
    this.gfx.x = this.body.x;
    this.gfx.y = this.body.y;
  }

  _explode() {
    sound.explode();
    // Push nearby dynamic bodies outward
    for (const b of this.container.__physBodies ?? []) {
      const dx = b.x - this.body.x;
      const dy = b.y - this.body.y;
      const dist = Math.sqrt(dx*dx + dy*dy) || 1;
      const radius = Math.max(this.config.w, this.config.h) * 4;
      if (dist < radius) {
        const force = (1 - dist / radius) * 1400;
        b.applyImpulse((dx / dist) * force * b.mass,
                       (dy / dist) * force * b.mass - 300 * b.mass);
      }
    }
    this._spawnParticles();
    this.destroy();
  }

  _spawnParticles() {
    for (let i = 0; i < 10; i++) {
      const p = new PIXI.Graphics();
      p.circle(0, 0, 6 + Math.random() * 10).fill({ color: 0xFF6600 });
      p.x = this.body.x;
      p.y = this.body.y;
      this.container.addChild(p);
      const angle = (i / 10) * Math.PI * 2;
      const speed = 200 + Math.random() * 300;
      let vx = Math.cos(angle) * speed;
      let vy = Math.sin(angle) * speed - 100;
      let life = 0.5 + Math.random() * 0.3;
      const tick = () => {
        const dt2 = 1/60;
        p.x += vx * dt2; p.y += vy * dt2; vy += 900 * dt2;
        life -= dt2; p.alpha = life / 0.8;
        if (life > 0) requestAnimationFrame(tick);
        else { this.container.removeChild(p); p.destroy(); }
      };
      requestAnimationFrame(tick);
    }
  }

  destroy() {
    this.alive = false;
    if (this.gfx.parent) this.gfx.parent.removeChild(this.gfx);
    this.gfx.destroy({ children: true });
  }
}
